import React, { useState, useEffect, useRef } from 'react';
import { signIn, signUp, confirmSignUp, confirmSignIn, resendSignUpCode, resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { get, post } from 'aws-amplify/api';
import './Login.css';

// デバイストークンをlocalStorageで管理
const getOrCreateDeviceToken = () => {
  let token = localStorage.getItem('factime_device_token');
  if (!token) {
    token = 'dt_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('factime_device_token', token);
  }
  return token;
};

const getDeviceHint = () => {
  const ua = navigator.userAgent;
  if (/iPhone|iPad/.test(ua)) return 'iPhone/iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Mac/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows';
  return 'ブラウザ';
};

// Cognito/サーバーからのエラーを分かりやすい日本語メッセージに変換する
// （想定していないエラーコードでも、生の英語メッセージをそのまま表示しないようにする）
const AUTH_ERROR_MESSAGES = {
  UsernameExistsException: 'このユーザー名は既に使用されています',
  InvalidPasswordException: 'パスワードは8文字以上で、大文字・小文字・数字を含める必要があります',
  InvalidParameterException: '入力内容に誤りがあります。メールアドレスや入力項目を確認してください',
  CodeDeliveryFailureException: '認証コードの送信に失敗しました。メールアドレスをご確認のうえ、しばらくしてから再度お試しください',
  UserLambdaValidationException: '登録内容を確認できませんでした。入力内容をご確認ください',
  UserNotFoundException: 'ユーザーが見つかりません。ユーザー名をご確認ください',
  NotAuthorizedException: 'ユーザー名またはパスワードが正しくありません',
  UserNotConfirmedException: 'メールアドレスの確認が完了していません。認証コードを入力してください',
  PasswordResetRequiredException: 'パスワードの再設定が必要です',
  CodeMismatchException: '認証コードが正しくありません',
  ExpiredCodeException: '認証コードの有効期限が切れています。再送してください',
  LimitExceededException: '試行回数が多すぎます。しばらく時間を置いてから再度お試しください',
  TooManyRequestsException: 'リクエストが多すぎます。しばらく時間を置いてから再度お試しください',
  TooManyFailedAttemptsException: '失敗回数が多すぎます。しばらく時間を置いてから再度お試しください',
  NetworkError: 'ネットワークエラーが発生しました。通信環境をご確認ください',
  InternalErrorException: 'サーバー内部でエラーが発生しました。しばらくしてから再度お試しください',
};

const translateAuthError = (error, fallback = '予期しないエラーが発生しました。しばらくしてから再度お試しください') => {
  if (error?.name && AUTH_ERROR_MESSAGES[error.name]) {
    return AUTH_ERROR_MESSAGES[error.name];
  }
  return fallback;
};

// ユーザー名はメールアドレス形式（半角英数字・ピリオド・@のみ、アンダースコア不可）に統一する
const USERNAME_PATTERN = /^[a-zA-Z0-9.]+@[a-zA-Z0-9.]+$/;

function Login({ onLoginSuccess, initialMode = 'login', onShowLanding, initialError = '' }) {
  const [mode, setMode] = useState(initialMode); // 'login', 'signup', 'confirm', 'reset', 'confirmReset', 'deviceOtp', 'mfaTotp', 'newPassword'
  const [loginStep, setLoginStep] = useState('username'); // 'username' | 'password' | 'webauthnPending'
  // パスキー認証に失敗したとき、パスワードでのログインを選べるようにする
  // （端末の紛失や、パスキーの関連付けドメイン変更後の再登録のため）
  const [passkeyFallback, setPasskeyFallback] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(initialError);
  const [alert, setAlert] = useState(null);
  const [deviceOtpInput, setDeviceOtpInput] = useState('');
  const [otpEmail, setOtpEmail] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0); // 再送クールダウン（秒）
  const cooldownRef = useRef(null);

  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    verificationCode: '',
    lastName: '',
    firstName: '',
    displayName: '',
    companyName: '',
    address: '',
    phone: '',
    fax: ''
  });

  // ログイン後にデバイス確認を行う
  const checkDeviceAfterLogin = async () => {
    // MFA設定状況をDBで確認
    let mfaEnabled = false;
    try {
      const op = get({ apiName: 'worktimerApi', path: '/users' });
      const res = await op.response;
      const users = await res.body.json();
      mfaEnabled = users?.[0]?.mfa_enabled ?? false;
    } catch (e) {
      // 確認失敗はそのままログイン成功扱い
      onLoginSuccess();
      return;
    }

    if (!mfaEnabled) {
      // MFA未設定 → App.jsのMFA設定オーバーレイへ
      onLoginSuccess();
      return;
    }

    // MFA設定済みはTOTPが第二認証要素のためデバイスOTPは不要
    onLoginSuccess();
  };

  const handleDeviceOtpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const deviceToken = getOrCreateDeviceToken();
      const op = post({
        apiName: 'worktimerApi',
        path: '/auth/verify-otp',
        options: { body: { device_token: deviceToken, otp: deviceOtpInput, device_hint: getDeviceHint() } }
      });
      await op.response;
      onLoginSuccess();
    } catch (e) {
      let msg = '認証コードが正しくないか期限切れです';
      try { const b = await e.response.body.json(); msg = b.message || msg; } catch {}
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    setError('');
    try {
      const deviceToken = getOrCreateDeviceToken();
      const op = post({
        apiName: 'worktimerApi',
        path: '/auth/send-otp',
        options: { body: { device_token: deviceToken, device_hint: getDeviceHint() } }
      });
      const res = await op.response;
      const data = await res.body.json();
      setOtpEmail(data.email || '');
      setAlert({ type: 'success', message: '認証コードを再送しました' });
    } catch (e) {
      setError('再送に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // Step1: ユーザー名のみ送信し、利用可能な認証方式(パスキー/パスワード)を判定する
  const handleUsernameSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setPasskeyFallback(false);

    try {
      console.log('🔵 signIn attempt (USER_AUTH):', formData.username);

      const { isSignedIn, nextStep } = await signIn({
        username: formData.username,
        options: { authFlowType: 'USER_AUTH' },
      });

      if (isSignedIn) {
        await checkDeviceAfterLogin();
        return;
      }

      if (nextStep?.signInStep === 'CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION') {
        const challenges = nextStep.availableChallenges || [];
        if (challenges.includes('WEB_AUTHN')) {
          // パスキー認証（ブラウザのダイアログがconfirmSignIn内で自動的に起動する）
          setLoginStep('webauthnPending');
          try {
            const result = await confirmSignIn({ challengeResponse: 'WEB_AUTHN' });
            if (result.isSignedIn) {
              await checkDeviceAfterLogin();
            } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
              // ユーザープールの設定によっては、パスキーの後にも認証アプリのコードが求められる
              setMode('mfaTotp');
            } else {
              setError('パスキー認証を完了できませんでした。');
              setLoginStep('username');
            }
          } catch (webauthnError) {
            console.error('❌ WebAuthn sign-in error:', webauthnError.name, webauthnError.message);
            setError(
              webauthnError.name === 'NotAllowedError'
                ? 'パスキー認証がキャンセルされました。'
                : 'パスキー認証に失敗しました。もう一度お試しください。'
            );
            if (challenges.includes('PASSWORD_SRP') || challenges.includes('PASSWORD')) setPasskeyFallback(true);
            setLoginStep('username');
          }
          return;
        }
        if (challenges.includes('PASSWORD_SRP') || challenges.includes('PASSWORD')) {
          // パスワード認証（SRP保護版を優先的に選択）
          await confirmSignIn({
            challengeResponse: challenges.includes('PASSWORD_SRP') ? 'PASSWORD_SRP' : 'PASSWORD',
          });
          setLoginStep('password');
          return;
        }
        setError('この端末で利用可能なログイン方式がありません。');
        return;
      }

      setError('ログインを完了できませんでした。再度お試しください。');
    } catch (error) {
      console.error('❌ Login error:', error.name, error.message);
      if (error.name === 'UserNotFoundException') {
        setError('ユーザー名またはパスワードが正しくありません');
      } else if (error.name === 'UserNotConfirmedException') {
        setError('メールアドレスが確認されていません。確認コードを入力してください。');
        setMode('confirm');
      } else if (error.name === 'TooManyRequestsException') {
        setError('試行回数が多すぎます。しばらく待ってから再度お試しください。');
      } else if (error.name === 'LimitExceededException') {
        setError('試行回数の上限に達しました。しばらく待ってから再度お試しください。');
      } else {
        setError('ログインに失敗しました。再度お試しください。');
      }
    } finally {
      setLoading(false);
    }
  };

  // パスキーが使えない場合：パスワード認証でサインインをやり直す
  const handlePasswordFallback = async () => {
    setLoading(true);
    setError('');
    try {
      const { nextStep } = await signIn({
        username: formData.username,
        options: { authFlowType: 'USER_AUTH', preferredChallenge: 'PASSWORD_SRP' },
      });
      if (nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_PASSWORD') {
        setPasskeyFallback(false);
        setLoginStep('password');
      } else {
        setError('パスワードでのログインを開始できませんでした。');
      }
    } catch (e) {
      console.error('❌ Password fallback error:', e.name, e.message);
      setError('パスワードでのログインを開始できませんでした。再度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  // Step2: パスワードを送信する
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { isSignedIn, nextStep } = await confirmSignIn({ challengeResponse: formData.password });

      if (isSignedIn) {
        await checkDeviceAfterLogin();
      } else if (nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        setMode('mfaTotp');
      } else if (nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        // 管理者作成ユーザーの初回ログイン: 仮パスワードから新パスワードへの変更が必要
        setMode('newPassword');
      } else {
        setError('ログインを完了できませんでした。再度お試しください。');
      }
    } catch (error) {
      console.error('❌ Password step error:', error.name, error.message);
      if (error.name === 'NotAuthorizedException') {
        setError('ユーザー名またはパスワードが正しくありません');
      } else if (error.name === 'PasswordResetRequiredException') {
        setError('パスワードのリセットが必要です。「パスワードを忘れた」から再設定してください。');
      } else if (error.name === 'TooManyRequestsException') {
        setError('試行回数が多すぎます。しばらく待ってから再度お試しください。');
      } else {
        setError('ログインに失敗しました。再度お試しください。');
      }
    } finally {
      setLoading(false);
    }
  };

  const backToUsernameStep = () => {
    setLoginStep('username');
    setError('');
    setFormData((prev) => ({ ...prev, password: '' }));
  };

  const handleMfaTotpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { isSignedIn } = await confirmSignIn({ challengeResponse: totpCode });
      if (isSignedIn) {
        await checkDeviceAfterLogin();
      } else {
        setError('認証に失敗しました。再度ログインしてください。');
        setMode('login');
        setTotpCode('');
      }
    } catch (error) {
      console.error('❌ MFA TOTP error:', error.name, error.message);
      if (error.name === 'CodeMismatchException') {
        setError('認証コードが正しくありません。認証アプリの6桁コードを確認してください。');
      } else if (error.name === 'NotAuthorizedException') {
        setError('セッションが切れました。再度ログインしてください。');
        setMode('login');
      } else if (error.name === 'TooManyRequestsException') {
        setError('試行回数が多すぎます。しばらく待ってから再度お試しください。');
      } else {
        setError('MFA認証に失敗しました。再度お試しください。');
      }
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  };

  // 初回ログイン時の新パスワード設定
  const handleNewPasswordSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      setError('パスワードが一致しません');
      return;
    }
    if (newPassword.length < 8) {
      setError('パスワードは8文字以上で入力してください');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { isSignedIn, nextStep } = await confirmSignIn({ challengeResponse: newPassword });
      if (isSignedIn) {
        await checkDeviceAfterLogin();
      } else if (nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        // MFA登録済みユーザー: 新パスワード設定後に認証アプリのコード入力へ進む
        setMode('mfaTotp');
      } else {
        console.error('❌ New password next step:', nextStep?.signInStep);
        setError('パスワードの設定に失敗しました。再度お試しください。');
        setMode('login');
      }
    } catch (error) {
      console.error('❌ New password error:', error.name, error.message);
      if (error.name === 'InvalidPasswordException') {
        setError('パスワードの要件を満たしていません（8文字以上、大文字・小文字・数字を含む）');
      } else if (error.name === 'NotAuthorizedException') {
        setError('セッションが切れました。再度ログインしてください。');
        setMode('login');
      } else {
        setError('パスワードの設定に失敗しました。再度お試しください。');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // ユーザー名チェック
    if (!formData.username) {
      setError('ユーザー名は必須です');
      setLoading(false);
      return;
    }
    if (!USERNAME_PATTERN.test(formData.username)) {
      setError('ユーザー名はメールアドレス形式（半角英数字・ピリオド・@のみ、アンダースコア不可）で入力してください');
      setLoading(false);
      return;
    }

    // パスワード確認
    if (formData.password !== formData.confirmPassword) {
      setError('パスワードが一致しません');
      setLoading(false);
      return;
    }

    // 会社名の必須チェック
    if (!formData.companyName) {
      setError('会社名は必須です');
      setLoading(false);
      return;
    }

    // 姓名の必須チェック
    if (!formData.lastName || !formData.firstName) {
      setError('姓と名は必須です');
      setLoading(false);
      return;
    }

    // 表示名の必須チェック
    if (!formData.displayName) {
      setError('表示名は必須です');
      setLoading(false);
      return;
    }

    try {
      console.log('📤 Signing up with username:', formData.username);

      const { isSignUpComplete, userId, nextStep } = await signUp({
        username: formData.username,
        password: formData.password,
        options: {
          userAttributes: {
            email: formData.email,
            'custom:last_name': formData.lastName,
            'custom:first_name': formData.firstName,
            'custom:display_name': formData.displayName,
            'custom:company_name': formData.companyName,
            'custom:address': formData.address || '',
            'custom:phone': formData.phone || '',
            'custom:fax': formData.fax || ''
          },
          autoSignIn: false
        }
      });

      console.log('✅ Sign up successful:', { isSignUpComplete, userId, nextStep });
      
      setAlert({
        type: 'success',
        message: '登録メールを送信しました。メールを確認して認証コードを入力してください。'
      });
      
      setMode('confirm');
    } catch (error) {
      console.error('❌ Sign up error:', error);
      setError(translateAuthError(error, '登録に失敗しました。しばらくしてから再度お試しください'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await confirmSignUp({
        username: formData.username,
        confirmationCode: formData.verificationCode
      });

      setAlert({
        type: 'success',
        message: 'メールアドレスが確認されました。ログインしてください。'
      });
      
      setMode('login');
      setFormData({ ...formData, verificationCode: '' });
    } catch (error) {
      console.error('❌ Confirm error:', error);
      if (error.name === 'CodeMismatchException') {
        setError('確認コードが正しくありません');
      } else if (error.name === 'ExpiredCodeException') {
        setError('確認コードの有効期限が切れています');
      } else {
        setError(translateAuthError(error, '確認に失敗しました。しばらくしてから再度お試しください'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendSignUpCode = async () => {
    if (resendCooldown > 0 || !formData.username) return;
    setLoading(true);
    setError('');
    try {
      await resendSignUpCode({ username: formData.username });
      setAlert({ type: 'success', message: '確認コードを再送しました。メールをご確認ください。' });
      startCooldown(60);
    } catch (error) {
      console.error('❌ Resend signup code error:', error);
      if (error.name === 'LimitExceededException') {
        setError('送信回数の上限に達しました。しばらく時間をおいてからお試しください。');
        startCooldown(120);
      } else {
        setError(translateAuthError(error, '確認コードの再送に失敗しました。しばらくしてから再度お試しください'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await resetPassword({ username: formData.username });

      setAlert({
        type: 'success',
        message: 'パスワードリセットコードを送信しました。メールを確認してください。'
      });
      startCooldown(60);
      setMode('confirmReset');
    } catch (error) {
      console.error('❌ Reset password error:', error);
      if (
        error.name === 'InvalidParameterException' &&
        error.message?.includes('cannot be reset in the current state')
      ) {
        setError(
          'まずメールに届いた仮パスワードでログインし、新しいパスワードを設定してください。' +
          'その後でパスワードのリセットが可能になります。'
        );
      } else if (error.name === 'UserNotFoundException') {
        setError('ユーザー名が見つかりません。正しいユーザー名を入力してください。');
      } else if (error.name === 'LimitExceededException') {
        setError('試行回数が多すぎます。しばらく待ってから再度お試しください。');
      } else {
        setError(translateAuthError(error, 'パスワードリセットに失敗しました。しばらくしてから再度お試しください'));
      }
    } finally {
      setLoading(false);
    }
  };

  // クールダウンタイマー
  useEffect(() => {
    if (resendCooldown <= 0) return;
    cooldownRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(cooldownRef.current);
  }, [resendCooldown > 0]);

  const startCooldown = (seconds = 60) => setResendCooldown(seconds);

  const handleResendResetCode = async () => {
    if (resendCooldown > 0 || !formData.username) return;
    setLoading(true);
    setError('');
    try {
      await resetPassword({ username: formData.username });
      setAlert({ type: 'success', message: 'リセットコードを再送しました。メールをご確認ください。' });
      startCooldown(60);
    } catch (error) {
      console.error('❌ Resend reset code error:', error);
      if (error.name === 'LimitExceededException') {
        setError('送信回数の上限に達しました。しばらく時間をおいてからお試しください。');
        startCooldown(120);
      } else {
        setError(translateAuthError(error, 'リセットコードの再送に失敗しました。しばらくしてから再度お試しください'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('パスワードが一致しません');
      setLoading(false);
      return;
    }

    try {
      await confirmResetPassword({
        username: formData.username,
        confirmationCode: formData.verificationCode,
        newPassword: formData.password
      });

      setAlert({
        type: 'success',
        message: 'パスワードがリセットされました。ログインしてください。'
      });
      
      setMode('login');
      setFormData({ email: formData.email, password: '', confirmPassword: '', verificationCode: '' });
    } catch (error) {
      console.error('❌ Confirm reset error:', error);
      if (error.name === 'CodeMismatchException') {
        setError('確認コードが正しくありません');
      } else if (error.name === 'ExpiredCodeException') {
        setError('確認コードの有効期限が切れています。再送してください');
      } else {
        setError(translateAuthError(error, 'パスワードリセットの確認に失敗しました。しばらくしてから再度お試しください'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-circle login-circle-1"></div>
      <div className="login-circle login-circle-2"></div>
      
      <div className={`login-card ${mode === 'signup' ? 'login-card--signup' : ''}`}>
        <div className="login-header">
          <img src="/FACTime_logo_Black.png" alt="FACTime" className="login-logo" />
          {onShowLanding && (
            <button
              type="button"
              onClick={onShowLanding}
              className="login-landing-link"
            >
              ← 製品紹介ページへ
            </button>
          )}
        </div>

        {alert && (
          <div className={`alert alert-${alert.type}`}>
            {alert.message}
          </div>
        )}

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {mode === 'login' && loginStep === 'username' && (
          <form onSubmit={handleUsernameSubmit} className="login-form">
            <div className="form-group">
              <label>ユーザー名</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="yamada.taro@example.com"
                required
                disabled={loading}
                autoComplete="username"
                autoFocus
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (
                <span className="btn-spinner-wrap">
                  <span className="btn-spinner" />
                  確認中...
                </span>
              ) : '次へ'}
            </button>

            {passkeyFallback && (
              <div className="passkey-fallback">
                <p>パスキーが使えない場合（端末を変えた・パスキーを削除した など）は、パスワードでログインしてパスキーを登録し直してください。</p>
                <button type="button" className="btn btn-secondary" onClick={handlePasswordFallback} disabled={loading}>
                  パスワードでログイン
                </button>
              </div>
            )}

            <div className="login-links">
              <button type="button" onClick={() => setMode('signup')} className="link-button">
                アカウントを作成
              </button>
              <button type="button" onClick={() => setMode('reset')} className="link-button">
                パスワードを忘れた
              </button>
            </div>
          </form>
        )}

        {mode === 'login' && loginStep === 'webauthnPending' && (
          <div className="login-form">
            <div className="webauthn-pending">
              <span className="btn-spinner" />
              <p>パスキーで認証しています。ブラウザのダイアログを確認してください。</p>
            </div>
            {error && <div className="form-error">{error}</div>}
            <button type="button" className="btn btn-primary" onClick={handleUsernameSubmit} disabled={loading}>
              パスキーで再試行
            </button>
            <div className="login-links">
              <button type="button" onClick={backToUsernameStep} className="link-button">
                ← ユーザー名を変更
              </button>
            </div>
          </div>
        )}

        {mode === 'login' && loginStep === 'password' && (
          <form onSubmit={handlePasswordSubmit} className="login-form">
            <div className="form-group">
              <label>パスワード</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                required
                disabled={loading}
                autoFocus
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (
                <span className="btn-spinner-wrap">
                  <span className="btn-spinner" />
                  ログイン中...
                </span>
              ) : 'ログイン'}
            </button>

            <div className="login-links">
              <button type="button" onClick={backToUsernameStep} className="link-button">
                ← ユーザー名を変更
              </button>
              <button type="button" onClick={() => setMode('reset')} className="link-button">
                パスワードを忘れた
              </button>
            </div>
          </form>
        )}

        {mode === 'signup' && (
          <form onSubmit={handleSignUp} className="login-form">
            <h2 className="form-title">新規登録</h2>

            <div className="form-row">
              <div className="form-group">
                <label>姓 <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  placeholder="山田"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>名 <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  placeholder="太郎"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-group">
              <label>表示名 <span className="required">*</span></label>
              <input
                type="text"
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                placeholder="山田 太郎"
                required
                disabled={loading}
              />
              <small>アプリ内で表示される名前です</small>
            </div>

            <div className="form-group">
              <label>会社名 <span className="required">*</span></label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                placeholder="株式会社〇〇"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label>住所</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="東京都渋谷区〇〇"
                disabled={loading}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>電話番号</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="03-1234-5678"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>FAX</label>
                <input
                  type="tel"
                  value={formData.fax}
                  onChange={(e) => setFormData({ ...formData, fax: e.target.value })}
                  placeholder="03-1234-5679"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-group">
              <label>ユーザー名 <span className="required">*</span></label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="yamada.taro@example.com（ログイン時に使用）"
                required
                disabled={loading}
              />
              <p style={{ fontSize: '0.78rem', color: '#6B7280', margin: '4px 0 0' }}>
                ※ ログイン時に使用します。メールアドレス形式（半角英数字・ピリオド・@のみ、アンダースコア不可）
              </p>
            </div>

            <div className="form-group">
              <label>メールアドレス <span className="required">*</span></label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="your@email.com"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label>パスワード <span className="required">*</span></label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="8文字以上"
                required
                disabled={loading}
              />
              <small>大文字・小文字・数字を含む8文字以上</small>
            </div>

            <div className="form-group">
              <label>パスワード確認 <span className="required">*</span></label>
              <input
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="もう一度入力"
                required
                disabled={loading}
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? '登録中...' : '登録する'}
            </button>

            <div className="login-links">
              <button type="button" onClick={() => { setMode('login'); setError(''); setAlert(null); }} className="link-button">
                ログインに戻る
              </button>
            </div>
          </form>
        )}

        {mode === 'confirm' && (
          <form onSubmit={handleConfirmSignUp} className="login-form">
            <h2 className="form-title">メール確認</h2>
            <p className="form-description">
              {formData.email
                ? `${formData.email} に送信された確認コードを入力してください`
                : 'ご登録のメールアドレスに送信された確認コードを入力してください'}
            </p>

            <div className="form-group">
              <label>確認コード</label>
              <input
                type="text"
                value={formData.verificationCode}
                onChange={(e) => setFormData({ ...formData, verificationCode: e.target.value })}
                placeholder="123456"
                required
                disabled={loading}
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? '確認中...' : '確認する'}
            </button>

            <div className="login-links">
              <button
                type="button"
                onClick={handleResendSignUpCode}
                className="link-button"
                disabled={loading || resendCooldown > 0}
                style={{ color: resendCooldown > 0 ? '#9CA3AF' : undefined }}
              >
                {resendCooldown > 0 ? `コードを再送 (${resendCooldown}秒後に再試行可)` : 'コードを再送する'}
              </button>
              <button type="button" onClick={() => { setMode('login'); setError(''); setAlert(null); }} className="link-button">
                ログインに戻る
              </button>
            </div>
          </form>
        )}

        {mode === 'reset' && (
          <form onSubmit={handleResetPassword} className="login-form">
            <h2 className="form-title">パスワードリセット</h2>

            <div className="form-group">
              <label>ユーザー名</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="yamada_taro"
                required
                disabled={loading}
              />
            </div>
            <p className="form-hint">登録済みのメールアドレスにリセットコードを送信します</p>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? '送信中...' : 'リセットコードを送信'}
            </button>

            <div className="login-links">
              <button type="button" onClick={() => { setMode('login'); setError(''); setAlert(null); }} className="link-button">
                ログインに戻る
              </button>
            </div>
          </form>
        )}

        {mode === 'confirmReset' && (
          <form onSubmit={handleConfirmResetPassword} className="login-form">
            <h2 className="form-title">新しいパスワード</h2>

            <div className="form-group">
              <label>確認コード</label>
              <input
                type="text"
                value={formData.verificationCode}
                onChange={(e) => setFormData({ ...formData, verificationCode: e.target.value })}
                placeholder="123456"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label>新しいパスワード</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="8文字以上"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label>パスワード確認</label>
              <input
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="もう一度入力"
                required
                disabled={loading}
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'リセット中...' : 'パスワードをリセット'}
            </button>

            <div className="login-links">
              <button
                type="button"
                onClick={handleResendResetCode}
                className="link-button"
                disabled={loading || resendCooldown > 0}
                style={{ color: resendCooldown > 0 ? '#9CA3AF' : undefined }}
              >
                {resendCooldown > 0 ? `コードを再送 (${resendCooldown}秒後に再試行可)` : 'コードを再送する'}
              </button>
              <button type="button" onClick={() => { setMode('login'); setError(''); setAlert(null); }} className="link-button">
                ログインに戻る
              </button>
            </div>
          </form>
        )}

        {mode === 'mfaTotp' && (
          <form onSubmit={handleMfaTotpSubmit} className="login-form">
            <h2 className="form-title">🔐 二段階認証</h2>
            <p className="form-description">
              認証アプリ（Google Authenticatorなど）に表示されている6桁のコードを入力してください。
            </p>

            <div className="form-group">
              <label>認証コード</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6桁の数字"
                required
                disabled={loading}
                autoFocus
                style={{ letterSpacing: '0.3em', fontSize: '1.4rem', textAlign: 'center' }}
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading || totpCode.length !== 6}>
              {loading ? (
                <span className="btn-spinner-wrap">
                  <span className="btn-spinner" />
                  確認中...
                </span>
              ) : '認証する'}
            </button>

            <div className="login-links">
              <button type="button" onClick={() => { setMode('login'); setTotpCode(''); setError(''); }} className="link-button" disabled={loading}>
                ← ログインに戻る
              </button>
            </div>
          </form>
        )}

        {mode === 'newPassword' && (
          <form onSubmit={handleNewPasswordSubmit} className="login-form">
            <h2 className="form-title">🔑 新しいパスワードを設定</h2>
            <p className="form-description">
              初回ログインのため、新しいパスワードを設定してください。<br />
              8文字以上で、大文字・小文字・数字を含めてください。
            </p>

            <div className="form-group">
              <label>新しいパスワード</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="新しいパスワード"
                required
                disabled={loading}
                autoFocus
              />
            </div>

            <div className="form-group">
              <label>パスワード確認</label>
              <input
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                placeholder="パスワードを再入力"
                required
                disabled={loading}
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading || !newPassword || !confirmNewPassword}>
              {loading ? (
                <span className="btn-spinner-wrap">
                  <span className="btn-spinner" />
                  設定中...
                </span>
              ) : 'パスワードを設定する'}
            </button>
          </form>
        )}

        {mode === 'deviceOtp' && (
          <form onSubmit={handleDeviceOtpSubmit} className="login-form">
            <h2 className="form-title">🔐 デバイス認証</h2>
            <p className="form-description">
              新しいデバイスからのログインを検出しました。<br />
              {otpEmail ? <><strong>{otpEmail}</strong> に送信された</> : '登録メールに送信された'}認証コードを入力してください。
            </p>

            <div className="form-group">
              <label>認証コード</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={deviceOtpInput}
                onChange={(e) => setDeviceOtpInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6桁の数字"
                required
                disabled={loading}
                style={{ letterSpacing: '0.3em', fontSize: '1.4rem', textAlign: 'center' }}
              />
              <small>コードは10分間有効です</small>
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading || deviceOtpInput.length !== 6}>
              {loading ? '確認中...' : '認証する'}
            </button>

            <div className="login-links">
              <button type="button" onClick={handleResendOtp} className="link-button" disabled={loading}>
                コードを再送する
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default Login;